<template>
  <b-card>
    <div>
      <!-- search input -->
      <b-row class="mb-1 align-items-center">
        <!-- Search input -->
        <b-col cols="12" sm="6" lg="4" class="mb-1 mb-sm-0">
          <b-form-input
              v-model.lazy="searchTerm"
              placeholder="Search..."
              type="text"
              class="d-inline-block w-100"
              @keyup="onSearch"
          />
        </b-col>

        <!-- Create button -->
        <b-col
            cols="12"
            sm="6"
            lg="8"
            class="d-flex justify-content-center justify-content-sm-end"
        >
            <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="flex-shrink-0"
                @click="showModal"
            >
              Create
            </b-button>
        </b-col>
      </b-row>

      <!-- table -->
      <vue-good-table
          styleClass="vgt-table table-custom-style striped"
          :line-numbers="false"
        mode="remote"
        @on-page-change="onPageChange"
        @on-sort-change="onSortChange"
        @on-column-filter="onColumnFilter"
        @on-per-page-change="onPerPageChange"
        :totalRows="totalRecords"
        :isLoading.sync="isLoading"
        :rows="rows"
        :columns="columns"
        :sort-options="{
          enabled: false,
          multipleColumns: true,
          initialSortBy: [
            { field: 'id', type: 'desc' },
            { field: 'created_at', type: 'desc' },
          ],
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template slot="table-row" slot-scope="props">
          <!-- Column: Action -->

          <template v-if="props.column.field === 'format_title'">
            <span>
                <b class="cursor-pointer" v-if="$permissionAbility(MEAL_POLICY_SHOW, permissions)" v-on:click="onShowPage(props.row)">{{ props?.row?.title }} </b>
                <b v-else>{{ props?.row?.title }} </b>
              </span>
          </template>

          <template v-if="props.column.field === 'format_amount_value_type'">

            <template v-if="props.row.amount_value_type == 'flat'">
              <b-badge variant="light-success">
                {{ (props.row.amount_value_type).toUpperCase() }}
              </b-badge>
            </template>
            <template v-else>
              <b-badge variant="light-warning">
                {{ (props.row.amount_value_type).toUpperCase() }}
              </b-badge>
            </template>
          </template>

          <template v-if="props.column.field === 'format_amount_value'">

            <template v-if="props.row.amount_value_type == 'percentage'">
              <b-badge variant="light-primary">
                {{ formatAmount(props.row) }}
              </b-badge>
            </template>
            <template v-else>
              <FormatCurrency :amount="props.row?.amount_value"  :currency="defaultCurrency" />
            </template>

          </template>

          <template v-if="props.column.field === 'format_currency'">

            <template v-if="props.row.amount_value_type !== 'percentage'">
              <FormatCurrency :type="'currency'"  :currency="defaultCurrency" />
            </template>
            <template v-else>
              -
            </template>

          </template>
          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <template
                  v-if="
                    $permissionAbility(MEAL_POLICY_EDIT, permissions)
                  "
                >
                  <b-dropdown-item v-on:click="onShow(props.row)">
                    <feather-icon icon="Edit2Icon" class="mr-50" />
                    <span>Edit</span>
                  </b-dropdown-item>
                </template>

                <template
                  v-if="
                    $permissionAbility(MEAL_POLICY_SHOW, permissions)
                  "
                >
                  <b-dropdown-item v-on:click="onShowPage(props.row)">
                    <feather-icon icon="EyeIcon" class="mr-50" />
                    <span>Show</span>
                  </b-dropdown-item>
                </template>
                <template
                  v-if="
                    $permissionAbility(MEAL_POLICY_DELETE, permissions)
                  "
                >
                  <b-dropdown-item v-on:click="onDelete(props.row.id)">
                    <feather-icon icon="TrashIcon" class="mr-50" />
                    <span>Delete</span>
                  </b-dropdown-item>
                </template>
              </b-dropdown>
            </span>
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                v-model="pageLength"
                :options="['10', '25', '50', '100', '500']"
                class="mx-1"
                @input="
                  (value) => props.perPageChanged({ currentPerPage: value })
                "
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>

    <b-modal
      id="modal-meal-policy-form"
      centered
      :title="
        modelType == 'editModel'
          ? 'Edit Meal Policy'
          : 'Create Meal Policy'
      "
      hide-footer
      @hidden="hiddenModal"
      no-close-on-backdrop
    >

      <validation-observer ref="addMealPolicy">
        <b-form v-on:submit.prevent="addMealPolicyForm">
          <!-- name -->
          <b-form-group label="Title" label-for="title" class="required-label">
            <validation-provider
              #default="{ errors }"
              name="title"
              vid="title"
              rules="required|max:255"
            >
              <b-form-input
                id="title"
                type="text"
                v-model="title"
                :state="errors.length > 0 ? false : null"
                placeholder="Enter Policy Title"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Type "
            label-for="amount_value_type"
            class="required-label"
          >
            <ValidationProvider
              name="type"
              v-slot="{ errors }"
              vid="amount_value_type"
              rules="required"
            >
              <v-select
                id="amount_value_type"
                v-model="amountValueType"
                :options="filteredMealAmountValueTypeConstants"
                :reduce="(option) => option.value"
                label="name"
                placeholder="Choose Here"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>

          <template v-if="amountValueType == 'percentage'">
            <b-form-group
              label="Amount (%)"
              label-for="amount_value"
              class="required-label"
            >
              <validation-provider
                #default="{ errors }"
                name="amount"
                vid="amount_value"
                rules="required|min_value:1|max_value:100"
              >
                <b-form-input
                  id="amount_value"
                  type="text"
                  v-model="amount"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Enter Amount"
                />

                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </template>

          <template v-else>
            <b-form-group
              label="Amount "
              label-for="amount_value"
              class="required-label"
            >
              <validation-provider
                #default="{ errors }"
                name="amount"
                vid="amount_value"
                rules="required|min_value:1"
              >
                <b-form-input
                  id="amount_value"
                  type="text"
                  v-model="amount"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Enter Amount"
                />

                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </template>

          <!-- loading button -->
          <template v-if="isMealPolicyLoading">
            <b-button class="float-right" variant="primary" disabled>
              <b-spinner small />
              Loading...
            </b-button>
          </template>

          <!-- submit button -->
          <template v-else>
            <b-button
              type="submit"
              class="float-right"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>

    <b-modal
      id="modal-policy-assign-user"
      centered
      title="Add Member To Policy"
      hide-footer
      @hidden="hiddenPolicyAssignModal"
      no-close-on-backdrop
    >
      <validation-observer ref="assignUserPolicy">
        <b-form v-on:submit.prevent="assignUserPolicyForm">
          <!-- name -->
          <b-form-group label="Title" label-for="title" class="required-label">
            <validation-provider
              #default="{ errors }"
              name="title"
              vid="title"
              rules="required|max:255"
            >
              <b-form-input
                id="title"
                type="text"
                v-model="title"
                :state="errors.length > 0 ? false : null"
                disabled
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Type "
            label-for="amount_value_type"
            class="required-label"
          >
            <ValidationProvider
              name="type"
              v-slot="{ errors }"
              vid="amount_value_type"
              rules="required"
            >
              <v-select
                id="amount_value_type"
                v-model="amountValueType"
                :options="filteredMealAmountValueTypeConstants"
                :reduce="(option) => option.value"
                label="name"
                disabled
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </b-form-group>

          <template v-if="amountValueType == 'percentage'">
            <b-form-group
              label="Amount (%)"
              label-for="amount_value"
              class="required-label"
            >
              <validation-provider
                #default="{ errors }"
                name="amount"
                vid="amount_value"
                rules="required|min_value:1"
              >
                <b-form-input
                  id="amount_value"
                  type="text"
                  v-model="amount"
                  :state="errors.length > 0 ? false : null"
                  disabled
                />

                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </template>

          <template v-else>
            <b-form-group
              label="Amount "
              label-for="amount_value"
              class="required-label"
            >
              <validation-provider
                #default="{ errors }"
                name="amount"
                vid="amount_value"
                rules="required|min_value:1"
              >
                <b-form-input
                  id="amount_value"
                  type="text"
                  v-model="amount"
                  :state="errors.length > 0 ? false : null"
                  disabled
                />

                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </template>



          <UserAssignPolicyForm
            @userAssignFromDataEvent="handleUserAssignFromData"
          />
          <template v-if="isUserPolicyAssignFormSubmitLoading">
            <b-button class="float-right" variant="primary" disabled>
              <b-spinner small />
              Loading...
            </b-button>
          </template>

          <!-- submit button -->
          <template v-else>
            <b-button
              type="submit"
              class="float-right"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
            >
              Assign
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

    <script>
    import {
      BCard,
      BAvatar,
      BBadge,
      BPagination,
      BFormGroup,
      BFormInput,
      BFormSelect,
      BDropdown,
      BDropdownItem,
      BButton,
      BForm,
      BModal,
      BSpinner,
      VBTooltip, BCol, BRow,
    } from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, max } from "@validations";
import { mealAmountValueTypeConstants } from "@/helpers/constant/MealAmountValueTypeConstant";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import UserAssignPolicyForm from "@/views/admin/payroll-management/UserAssignPolicyForm.vue";
import FormatCurrency from "@/layouts/components/FormatCurrency.vue";

import {
  MEAL_POLICY_CREATE,
  MEAL_POLICY_EDIT,
  MEAL_POLICY_DELETE,
  MEAL_POLICY_SHOW,
} from "@/helpers/permissionsConstant";

export default {
  name: "MealPolicyView",
  components: {
    BRow,
    BCol,
    UserAssignPolicyForm,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    FormatCurrency
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      MEAL_POLICY_CREATE,
      MEAL_POLICY_EDIT,
      MEAL_POLICY_DELETE,
      MEAL_POLICY_SHOW,

      defaultCurrency: "",

      modelType: "",
      divisionId: "",
      name: "",
      selectHeadId: "",
      headIdOption: [],

      projectCommissionPolicyId: "",
      salaryType: "",
      filteredMealAmountValueTypeConstants: [],
      mealAmountValueTypeConstants,
      amountValueType: "",
      title: "",
      amount: "",
      policyCategoryId: "",
      pageLength: 10,
      columns: [
        {
          label: "Title",
          field: "format_title",
          sortable: false,
        },
        {
          label: "Type",
          field: "format_amount_value_type",
          sortable: false,
          formatFn: this.formatAmountType,
        },
        {
          label: "Amount",
          field: "format_amount_value",
          sortable: true,
        },
        {
          label: "Currency",
          field: "format_currency",
          sortable: false,
        },
        {
          label: "Created On",
          field: "created_at",
          sortable: true,
          formatFn: this.formatDateTime,
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: "",
      delayTimer: null,
      isLoading: false,
      isMealPolicyLoading: false,
      isUserPolicyAssignFormSubmitLoading: false,
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: [
          { field: "id", type: "desc" },
          { field: "created_at", type: "desc" },
        ],
        page: 1,
        perPage: 10,
      },
    };
  },

  computed: {
    ...mapGetters({
      permissions: "userModule/getPermissions",
    }),
  },

  async created() {
    try {
      const [getSetting, getCurrencies] =
          await Promise.all([
            this.getSetting({
              select:
                  "currency_id",
            }),
            this.getCurrencies(),
          ]);

      // getSetting
      const currencyId = getSetting?.data?.data?.currency_id;

      // getCurrencies
      const currencyData = getCurrencies?.data?.data?.filter((item) => {
        return item?.id === currencyId
      });

      this.defaultCurrency = currencyData?.[0]?.currency;
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message,
        },
      });
    }
  },

  methods: {
    async getSetting(params) {
      return await this.$api.get("/api/settings", {
        params: {
          select: params.select,
        },
      });
    },
    async getCurrencies() {
      return await this.$api.get("/api/currencies/all");
    },

    onShowPage(value) {
      let policyCategoryName = "meal-policy";

      console.log(value)

      let policyCategoryId = value?.policy_category_id;
      let id = value?.id;
      this.$router.push({
        name: "admin-payroll-policy-assign-details",
        params: { policyCategoryName, policyCategoryId, id },
      });
    },
    handleUserAssignFromData(data) {
      this.policyAssignData = data;
    },
    showAssignUserModal() {
      this.$bvModal.show("modal-policy-assign-user");
    },

    hiddenPolicyAssignModal() {
      this.$bvModal.hide("modal-policy-assign-user");
    },
    onShowAssignUserModal(value) {
      this.policyCategoryId = value?.policy_category_id;
      this.projectCommissionPolicyId = value?.id;
      this.amountValueType = value?.amount_value_type;
      this.title = value?.title;
      this.amount = value?.amount_value;

      this.showAssignUserModal();
    },
    showModal() {
      this.filterAmountType();
      this.$bvModal.show("modal-meal-policy-form");
    },
    filterAmountType() {
      this.filteredMealAmountValueTypeConstants = this.mealAmountValueTypeConstants
    },

    hiddenModal() {
      this.modelType = "";
      this.$bvModal.hide("modal-meal-policy-form");
      this.resetModal();
    },
    resetModal() {
      this.projectCommissionPolicyId = "";
      this.amountValueType = "";
      this.title = "";
      this.amount = "";
    },
    async onShow(value) {
      this.modelType = "editModel";

      this.projectCommissionPolicyId = value?.id;
      this.amountValueType = value?.amount_value_type;
      this.title = value?.title;
      this.amount = value?.amount_value;

      this.showModal();
    },
    async onDelete(id) {
      this.$swal({
        title: "Warning!",
        text: "Are You Sure You Want To Delete This?",
        icon: "warning",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Ok",
        showLoaderOnConfirm: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.$api.delete(`/api/meal-policies/${id}`);

            this.loadItems();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Successfully Deleted",
              },
            });
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message,
              },
            });
          }
        }
      });
    },

    formatAmount(row) {
      if (row?.amount_value_type == "percentage") {
        return `${row?.amount_value} %`;
      }
      return row?.amount_value;
    },
    formatAmountType(value) {
      const amountType = mealAmountValueTypeConstants.find(
        (item) => item.value === value
      );
      return amountType?.name;
    },


    formatDateTime(value) {
      if (value) {
        return this.$moment(value).format("MMM Do YYYY");
      }
      return "Not Set";
    },
    onSearch() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer);
        this.delayTimer = null;
      }

      this.delayTimer = setTimeout(() => {
        this.loadItems();
      }, 1000);
    },

    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.loadItems();
    },

    onSortChange(params) {
      this.updateParams({
        sort: params,
      });
      this.loadItems();
    },

    onColumnFilter(params) {
      this.updateParams(params);
      this.loadItems();
    },

    async getMealPolicy(params) {
      return await this.$api.get("api/meal-policies", {
        params: {
          show: params.show,
          page: params.page,
          sort: params.sort,
          q: params.q,
        },
      });
    },
    async getUsers() {
      return await this.$api.get("api/users/all?include=designation");
    },

    async loadItems() {
      try {
        const projectCommissionPolicies = await this.getMealPolicy(
          {
            show: this.serverParams.perPage,
            page: this.serverParams.page,
            sort: this.serverParams.sort,
            q: this.searchTerm,
          }
        );

        const data = projectCommissionPolicies?.data?.data;
        const meta = projectCommissionPolicies?.data?.meta;

        this.totalRecords = meta?.pagination?.total;
        this.rows = data;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            icon: "BellIcon",
            variant: "warning",
            text: error?.response?.data?.message,
          },
        });
      }
    },
    addMealPolicyForm: async function () {
      this.$refs.addMealPolicy.validate().then(async (success) => {
        if (success) {
          try {
            if (this.modelType == "editModel") {
              this.isMealPolicyLoading = true;
              await this.$api.put(
                `/api/meal-policies/${this.projectCommissionPolicyId}`,

                {
                  title: this.title,
                  amount_value_type: this.amountValueType,
                  amount_value: this.amount,

                }
              );
              this.isMealPolicyLoading = false;
              this.hiddenModal();
              this.loadItems();

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Success",
                  icon: "BellIcon",
                  variant: "success",
                  text: "Meal Policy Successfully Updated",
                },
              });
            } else {
              this.isMealPolicyLoading = true;

              await this.$api.post(
                "/api/meal-policies",

                {
                  title: this.title,
                  amount_value_type: this.amountValueType,
                  amount_value: this.amount,

                }
              );
              this.isMealPolicyLoading = false;
              this.hiddenModal();

              this.loadItems();

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Success",
                  icon: "BellIcon",
                  variant: "success",
                  text: "Meal Policy Successfully Created",
                },
              });
            }
          } catch (error) {
            this.isMealPolicyLoading = false;
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }

            if (error?.response?.data?.errors) {
              this.$refs.addMealPolicy.setErrors(
                error?.response?.data?.errors
              );
            }
          }
        }
      });
    },

    assignUserPolicyForm: async function () {
      this.$refs.assignUserPolicy.validate().then(async (success) => {
        if (success) {
          try {
            this.$refs.assignUserPolicy.reset();

            this.isUserPolicyAssignFormSubmitLoading = true;

            await this.$api.post("/api/user-assign-policy", {
              policy_category_id: this.policyCategoryId,
              policy_id: this.projectCommissionPolicyId,
              applied_to: this.policyAssignData?.appliedTo,
              is_all_department_selected:
                this.policyAssignData?.isAllDeptSelected,
              is_all_user_selected: this.policyAssignData?.isAllUserSelected,
              department_ids: this.policyAssignData?.departmentIds,
              except_user_ids: this.policyAssignData?.exceptUserIds,
              user_ids: this.policyAssignData?.userIds,
            });

            this.isUserPolicyAssignFormSubmitLoading = false;
            this.hiddenPolicyAssignModal();
            this.loadItems();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Policy Assigned Successfully ",
              },
            });
          } catch (error) {
            this.isUserPolicyAssignFormSubmitLoading = false;
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }

            if (error?.response?.data?.errors) {
              this.$refs.assignUserPolicy.setErrors(
                error?.response?.data?.errors
              );
            }
          }
        }
      });
    },
  },
};
</script>

    <style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

.required-label label::after {
  content: " *";
  color: red;
}
</style>
